import React, { useState } from 'react';

interface FormData {
  name: string;
  email: string;
  phone: string;
  message: string;
}

const HomePage: React.FC = () => {
  const [activeAccordion, setActiveAccordion] = useState<number | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleAccordionToggle = (index: number) => {
    setActiveAccordion(activeAccordion === index ? null : index);
  };

  const painPoints = [
    {
      question: 'Exercise feels harder than expected or easily tired?',
      answer:
        "Our progressive intensity approach starts at your current level and gradually builds your endurance. Perfect for adults 40-70 who want sustainable fitness improvements without overwhelming workouts.",
      icon: '😰',
    },
    {
      question: 'Low confidence and worry you\'re not fit enough to start?',
      answer:
        "Perfect! I.C.E. is designed specifically for adults 40-70 looking to restart or enhance their fitness journey. Our welcoming community and expert trainers create a judgment‑free environment where you'll build confidence from day one.",
      icon: '💪',
    },
    {
      question: 'Worry about getting injured or worsening an existing injury?',
      answer:
        'Safety first! Our certified trainers understand the unique needs of adults 40-70 and assess your individual needs, modifying exercises accordingly. We focus on proper form and gradual progression to prevent injuries.',
      icon: '🏥',
    },
    {
      question: 'Too busy with work and family commitments?',
      answer:
        'I.C.E. sessions are designed to respect your busy life. Perfect for busy adults juggling career and family responsibilities. Our flexible scheduling and time‑efficient workouts fit perfectly into the busiest lifestyles.',
      icon: '⏰',
    },
    {
      question: 'Lose motivation when results take time?',
      answer:
        "With I.C.E., adults 40-70 see improvements within the first week! Our science‑based approach delivers faster results that are sustainable and age-appropriate, keeping you motivated and engaged.",
      icon: '📈',
    },
    {
      question: 'Fear of judgment from others?',
      answer:
        'Our supportive community celebrates every victory, big or small. Many of our members are professionals and parents just like you. You\'ll find encouragement, not judgment. Everyone started somewhere!',
      icon: '🤗',
    },
  ];

  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-transparent hover:bg-white hover:shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img
                src="https://static.readdy.ai/image/8e8cc12d0a4e0011e73079a8a1f6a96c/305a90f4c7c1c2b3e931b09382ba17d7.png"
                alt="Mana Fitness Solutions"
                className="h-8 sm:h-10 w-auto"
              />
              <span className="text-lg sm:text-xl font-bold text-gray-900">
                Mana Fitness Solutions
              </span>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#program" className="text-gray-700 hover:text-[#382112] cursor-pointer">
                Program
              </a>
              <a href="#benefits" className="text-gray-700 hover:text-[#382112] cursor-pointer">
                Benefits
              </a>
              <a href="#founders" className="text-gray-700 hover:text-[#382112] cursor-pointer">
                Meet the Team
              </a>
              <a href="#testimonials" className="text-gray-700 hover:text-[#382112] cursor-pointer">
                Success Stories
              </a>
              <a href="#location" className="text-gray-700 hover:text-[#382112] cursor-pointer">
                Location
              </a>
              <a
                href="https://form.typeform.com/to/slBoUFJW"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#382112] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#2d1a0e] cursor-pointer whitespace-nowrap"
              >
                Start Free Trial
              </a>
            </div>

            {/* Mobile Hamburger Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center text-gray-900 cursor-pointer"
            >
              <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-2xl`} />
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden absolute top-full left-0 right-0 bg-white shadow-lg py-4 px-4">
              <div className="flex flex-col space-y-4">
                <a href="#program" onClick={() => setMobileMenuOpen(false)} className="text-gray-700 hover:text-[#382112] cursor-pointer py-2">
                  Program
                </a>
                <a href="#benefits" onClick={() => setMobileMenuOpen(false)} className="text-gray-700 hover:text-[#382112] cursor-pointer py-2">
                  Benefits
                </a>
                <a href="#founders" onClick={() => setMobileMenuOpen(false)} className="text-gray-700 hover:text-[#382112] cursor-pointer py-2">
                  Meet the Team
                </a>
                <a href="#testimonials" onClick={() => setMobileMenuOpen(false)} className="text-gray-700 hover:text-[#382112] cursor-pointer py-2">
                  Success Stories
                </a>
                <a href="#location" onClick={() => setMobileMenuOpen(false)} className="text-gray-700 hover:text-[#382112] cursor-pointer py-2">
                  Location
                </a>
                <a
                  href="https://form.typeform.com/to/slBoUFJW"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#382112] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#2d1a0e] cursor-pointer whitespace-nowrap text-center"
                >
                  Start Free Trial
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://static.readdy.ai/image/8e8cc12d0a4e0011e73079a8a1f6a96c/27c63494517bcc6ebfb8d4c82bcc30fb.jpeg')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-gray-900/50" />
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 py-20 pt-24">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="space-y-6 sm:space-y-8 text-center lg:text-left">
              <div className="inline-block bg-[#382112] text-white px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-semibold tracking-wide">
                7-DAY FREE TRIAL • ADULTS 40-70
              </div>

              <div className="space-y-4">
                <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white leading-tight">
                  Finally, A Program That Helps You Regain Control of Your Life &amp; Health That You Can Start Now
                </h1>
                <p className="text-lg sm:text-xl text-gray-200 max-w-lg mx-auto lg:mx-0">
                  A science-based program for adults 40–70 who want real results, more energy, and confidence again — without wasting time or guessing what to do.
                </p>
              </div>

              {/* Mobile Only - How I.C.E. Works Video */}
              <div className="block lg:hidden">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4">
                  <div className="aspect-video bg-gray-800 rounded-xl overflow-hidden relative">
                    <iframe
                      width="100%"
                      height="100%"
                      src="https://www.youtube.com/embed/uVGmd6N1Brk?rel=0&controls=0&modestbranding=1&showinfo=0&iv_load_policy=3"
                      title="I.C.E. Program Explained"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      className="absolute inset-0 w-full h-full"
                    />
                  </div>
                  <div className="text-center mt-3">
                    <h3 className="text-white font-semibold text-base">How I.C.E. Works</h3>
                    <p className="text-gray-300 text-sm">Discover how I.C.E will work for you and how easy it is to get started</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border-l-4 border-[#d4a574] text-left">
                <p className="text-white font-semibold text-base sm:text-lg">
                  I.C.E. is for adults between 40-70 who want to create change <strong>now</strong> (not next month, not tomorrow). Enquiry today for a call back to see if I.C.E is the right fit.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <a
                  href="https://form.typeform.com/to/slBoUFJW"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#382112] text-white px-6 sm:px-8 py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-[#2d1a0e] transition-colors flex items-center justify-center space-x-2 cursor-pointer whitespace-nowrap"
                >
                  <span>START YOUR 7 DAY TRIAL NOW</span>
                  <i className="ri-arrow-right-line" />
                </a>
              </div>

              <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center lg:justify-start gap-2 sm:gap-4 text-white">
                <div className="flex space-x-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i key={star} className="ri-star-fill text-yellow-400" />
                  ))}
                </div>
                <span className="text-gray-200 text-sm sm:text-base text-center">
                  Join 500+ members aged 40-70 who are changing their lives with I.C.E
                </span>
              </div>
            </div>

            {/* Desktop Video */}
            <div className="hidden lg:block">
              <div className="relative">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 sm:p-6 mb-6">
                  <div className="aspect-video bg-gray-800 rounded-xl overflow-hidden relative">
                    <iframe
                      width="100%"
                      height="100%"
                      src="https://www.youtube.com/embed/uVGmd6N1Brk?rel=0&controls=0&modestbranding=1&showinfo=0&iv_load_policy=3"
                      title="I.C.E. Program Explained"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      className="absolute inset-0 w-full h-full"
                    />
                  </div>
                  <div className="text-center mt-4">
                    <h3 className="text-white font-semibold text-lg">How I.C.E. Works</h3>
                    <p className="text-gray-300 text-sm">Discover how I.C.E will work for you and how easy it is to get started</p>
                  </div>
                </div>

                {/* Stats */}
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 sm:p-8 space-y-4">
                  <div className="flex items-center space-x-4">
                    <div className="bg-[#382112] rounded-full p-3">
                      <i className="ri-fire-line text-white text-2xl" />
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-white">87%</div>
                      <div className="text-gray-200">Fat Loss Rate</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="bg-orange-500 rounded-full p-3">
                      <i className="ri-flashlight-line text-white text-2xl" />
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-white">3x</div>
                      <div className="text-gray-200">Faster Results</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Program Overview Section */}
      <section id="program" className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <div className="inline-block border border-gray-300 text-gray-600 px-4 py-2 rounded-full text-sm font-semibold tracking-wide mb-6">
              THE PROGRAM • ADULTS 40-70
            </div>
            <h2 className="text-5xl font-bold text-gray-900 mb-4">What is I.C.E.?</h2>
            <div className="w-20 h-1 bg-[#382112] mx-auto mb-6" />
            <p className="text-xl text-gray-600 mb-4">Integrated Cardio Exercise for Adults 40-70 Who Want Change Now</p>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              I.C.E is a science‑based &amp; results based program specifically designed for men and women aged 40-70, maximizing fitness and fat loss results in minimal time. Short intervals done at a challenging but manageable intensity to suit 'your' level, pace and comfort. Perfect for adults who want to create change now, not next month or tomorrow.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-8 rounded-xl text-center">
              <div className="w-20 h-20 bg-[#382112] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-flask-line text-white text-3xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Science-Based</h3>
              <p className="text-gray-700 leading-relaxed">
                Maximizes fitness and fat loss results through proven methodologies backed by exercise science research, specifically tailored for the 40-70 age group.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl text-center">
              <div className="w-20 h-20 bg-[#382112] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-settings-3-line text-white text-3xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Your Pace, Your Level</h3>
              <p className="text-gray-700 leading-relaxed">
                Short intervals at challenging but manageable intensity suited to your individual comfort and fitness level, perfect for mature adults seeking sustainable results.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl text-center">
              <div className="w-20 h-20 bg-[#382111] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-bar-chart-2-line text-white text-3xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Proven Results</h3>
              <p className="text-gray-700 leading-relaxed">
                Minimal time investment for maximum fitness transformation with measurable, lasting results designed for busy professionals and parents.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <a
              href="https://form.typeform.com/to/slBoUFJW"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#382112] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#2d1a0e] transition-colors cursor-pointer whitespace-nowrap"
            >
              DISCOVER YOUR PROGRAM
            </a>
          </div>
        </div>
      </section>

      {/* Get a Sneak Peak Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-4">
              Get a Sneak Peak of What I.C.E. Looks Like
            </h2>
            <p className="text-2xl text-[#d4a574]">
              See Real I.C.E. Classes for Adults 40-70 in Action
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Class Video */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
              <div className="aspect-video bg-gray-800 rounded-xl overflow-hidden relative mb-6">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/kwsh-zodqfg?rel=0&controls=0&modestbranding=1&showinfo=0&iv_load_policy=3"
                  title="I.C.E. Class Sneak Peak"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                />
              </div>
              <div className="text-center">
                <h3 className="text-white font-bold text-2xl mb-2">Live I.C.E. Class</h3>
                <p className="text-gray-300 text-lg">Get a sneak peek of one of our many I.C.E sessions designed for adults just like you</p>
              </div>
            </div>

            {/* Highlights */}
            <div className="space-y-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-[#382112] rounded-full flex items-center justify-center">
                    <i className="ri-timer-line text-white text-2xl" />
                  </div>
                  <h3 className="text-white text-xl font-bold">40 minute Sessions</h3>
                </div>
                <p className="text-gray-300">
                  Perfect duration for busy adults 40-70. Short, intense bursts that fit into any schedule without overwhelming your day.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-[#382112] rounded-full flex items-center justify-center">
                    <i className="ri-team-line text-white text-2xl" />
                  </div>
                  <h3 className="text-white text-xl font-bold">Age-Appropriate Expert Guidance</h3>
                </div>
                <p className="text-gray-300">
                  Jose &amp; Ana's 20+ years of experience ensures proper form and safe modifications specifically for mature adults.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-[#382112] rounded-full flex items-center justify-center">
                    <i className="ri-heart-pulse-line text-white text-2xl" />
                  </div>
                  <h3 className="text-white text-xl font-bold">Adaptive Intensity</h3>
                </div>
                <p className="text-gray-300">
                  Work at your own pace while still getting maximum cardiovascular and fat‑burning benefits designed for your age group.
                </p>
              </div>

              <div className="text-center">
                <a
                  href="https://form.typeform.com/to/slBoUFJW"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#382112] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#2d1a0e] transition-colors inline-block cursor-pointer whitespace-nowrap"
                >
                  EXPERIENCE I.C.E. YOURSELF
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Meet the Founder & Coaches Section */}
      <section id="founders" className="py-20 bg-gradient-to-b from-[#f5f0eb] to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block border border-gray-300 text-gray-600 px-4 py-2 rounded-full text-sm font-semibold tracking-wide mb-6">
              MEET THE TEAM
            </div>
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Founder &amp; Coaches</h2>
            <div className="w-20 h-1 bg-[#382112] mx-auto mb-6" />
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Meet Jose and Ana, your transformation experts with 20+ years of experience helping adults 40-70 achieve their fitness goals
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="grid md:grid-cols-2 gap-0">
                {/* Image */}
                <div className="relative h-96 md:h-auto">
                  <img
                    src="https://static.readdy.ai/image/8e8cc12d0a4e0011e73079a8a1f6a96c/e96c9baaf19d00be4c10420efb1ea1b4.jpeg"
                    alt="Jose and Ana - Founders of Mana Fitness Solutions"
                    className="w-full h-full object-contain bg-gray-50"
                  />
                </div>

                {/* Content */}
                <div className="p-12 flex flex-col justify-center">
                  <div className="space-y-6">
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="w-16 h-16 bg-[#382112] rounded-full flex items-center justify-center">
                        <i className="ri-award-line text-white text-3xl" />
                      </div>
                      <div>
                        <h3 className="text-3xl font-bold text-gray-900">Jose &amp; Ana</h3>
                        <p className="text-[#382112] font-semibold">Transformation Experts for Adults 40-70</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center space-x-3">
                        <i className="ri-check-line text-[#382112] text-xl" />
                        <span className="text-gray-700 font-semibold">20+ years experience as transformation experts</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <i className="ri-check-line text-[#382112] text-xl" />
                        <span className="text-gray-700 font-semibold">Certified strength &amp; conditioning coaches</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <i className="ri-check-line text-[#382112] text-xl" />
                        <span className="text-gray-700 font-semibold">Qualified nutrition specialists</span>
                      </div>
                    </div>

                    <div className="bg-[#f5f0eb] p-6 rounded-lg">
                      <p className="text-gray-700 text-lg leading-relaxed italic">
                        "Two of the best humans you'll ever interact with"
                      </p>
                      <p className="text-[#382112] font-semibold mt-2 text-sm">
                        - Feedback from our Mana crew 😉
                      </p>
                    </div>

                    <p className="text-gray-600 leading-relaxed">
                      Jose and Ana specialise in helping adults 40-70 rediscover their strength, confidence, and vitality. Their expertise in age‑appropriate fitness and nutrition ensures you get results safely and sustainably. <strong>Ready to create change now?</strong>
                    </p>

                    <div className="pt-4">
                      <a
                        href="https://form.typeform.com/to/slBoUFJW"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-[#382112] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#2d1a0e] transition-colors inline-block cursor-pointer whitespace-nowrap"
                      >
                        ENQUIRE FOR CALL BACK
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">Who is I.C.E. for?</h2>
            <p className="text-xl text-gray-600">Designed for men and women aged 40-70 with real lives who want to create change <strong>now</strong></p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="text-center mb-6">
                <div className="w-24 h-24 bg-[#f5f0eb] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-briefcase-line text-[#382112] text-4xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Busy Professionals</h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line text-[#382112]" />
                  <span className="text-gray-700">Time efficient workouts</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line text-[#382112]" />
                  <span className="text-gray-700">Flexible scheduling for busy lifestyles</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line text-[#382112]" />
                  <span className="text-gray-700">Perfect for career-focused adults</span>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="text-center mb-6">
                <div className="w-24 h-24 bg-[#f5f0eb] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-user-heart-line text-[#382112] text-4xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Fitness Restarters</h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line text-[#382112]" />
                  <span className="text-gray-700">Safe return to fitness after 40</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line text-[#382112]" />
                  <span className="text-gray-700">Confidence building approach</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line text-[#382112]" />
                  <span className="text-gray-700">Age-appropriate progressions</span>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="text-center mb-6">
                <div className="w-24 h-24 bg-[#f5f0eb] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-flashlight-line text-[#382112] text-4xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Active Parents</h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line text-[#382112]" />
                  <span className="text-gray-700">Maintain energy for family life</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line text-[#382112]" />
                  <span className="text-gray-700">Stress relief and mental wellness</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line text-[#382112]" />
                  <span className="text-gray-700">Set a healthy example</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-4">Triple Your Results After 40</h2>
            <p className="text-2xl text-[#d4a574]">The I.C.E. Advantage for Adults 40-70</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-b from-gray-800 to-gray-900 p-10 rounded-2xl">
              <div className="text-8xl font-bold bg-gradient-to-r from-[#d4a574] to-[#382112] bg-clip-text text-transparent mb-4">
                3x
              </div>
              <h3 className="text-3xl font-bold text-white mb-4">Fat Burning Power</h3>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                Burn up to 3 times more fat during cardio exercise compared to traditional methods - especially effective for adults over 40
              </p>
              <div className="flex justify-end">
                <i className="ri-fire-line text-[#d4a574] text-4xl" />
              </div>
            </div>

            <div className="bg-gradient-to-b from-gray-800 to-gray-900 p-10 rounded-2xl">
              <div className="w-20 h-20 bg-[#382112] rounded-full flex items-center justify-center mb-6">
                <i className="ri-heart-pulse-line text-white text-4xl" />
              </div>
              <h3 className="text-3xl font-bold text-white mb-4">Enhanced Endurance</h3>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                Build cardiovascular strength and stamina that carries into every aspect of your life - perfect for staying active with family and career
              </p>
              <div className="bg-gray-700 rounded-full h-4 mb-2">
                <div className="bg-[#d4a574] h-4 rounded-full" style={{ width: '85%' }} />
              </div>
              <span className="text-[#d4a574] text-sm">85% Improvement Average</span>
            </div>

            <div className="bg-gradient-to-b from-gray-800 to-gray-900 p-10 rounded-2xl">
              <div className="w-20 h-20 bg-[#382112] rounded-full flex items-center justify-center mb-6">
                <i className="ri-body-scan-line text-white text-4xl" />
              </div>
              <h3 className="text-3xl font-bold text-white mb-4">Preserve Muscle</h3>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                Maintain lean muscle mass while efficiently burning fat - crucial for adults 40-70 to maintain strength and metabolism
              </p>
              <div className="flex space-x-4 items-center">
                <div className="text-center">
                  <div className="text-sm text-gray-400 mb-1">Before</div>
                  <div className="w-8 h-12 bg-gray-600 rounded" />
                </div>
                <i className="ri-arrow-right-line text-[#d4a574]" />
                <div className="text-center">
                  <div className="text-sm text-gray-400 mb-1">After</div>
                  <div className="w-8 h-12 bg-[#d4a574] rounded" />
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <a
              href="https://form.typeform.com/to/slBoUFJW"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#382112] text-white px-10 py-5 rounded-lg text-xl font-bold hover:bg-[#2d1a0e] transition-colors cursor-pointer whitespace-nowrap"
            >
              START YOUR TRANSFORMATION
            </a>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="text-[#382112] font-semibold text-sm tracking-wide uppercase mb-4">
              SUCCESS STORIES
            </div>
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Real People, Real Results</h2>
            <p className="text-xl text-gray-600">
              Join the 100's of adults who have change their lives with our I.C.E program
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-pink-400 to-pink-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold">B</span>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Belinda, 40's</div>
                  <div className="text-gray-500 text-sm">Mum</div>
                </div>
              </div>
              
              <div className="mb-6 rounded-xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/8e8cc12d0a4e0011e73079a8a1f6a96c/780d3fbff20550c3442d7564c1712391.png"
                  alt="Belinda's transformation - before and after"
                  className="w-full h-64 object-cover"
                />
              </div>
              
              <p className="text-gray-700 leading-relaxed mb-4">
                "You have nothing to lose and everything to gain. Whether you are seeking better health or fitness or have specific goals ... you'll never know, if you don't give it a go. And believe me when I say, you are worthy of taking the time to focus on you."
              </p>
              <div className="flex space-x-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i key={star} className="ri-star-fill text-yellow-400" />
                ))}
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold">D</span>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Dom, 40's</div>
                  <div className="text-gray-500 text-sm">IT Specialist</div>
                </div>
              </div>
              
              <div className="mb-6 rounded-xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/8e8cc12d0a4e0011e73079a8a1f6a96c/565b539394d30bb69476ce34b969e11e.png"
                  alt="Dom's transformation - before and after"
                  className="w-full h-64 object-cover"
                />
              </div>
              
              <p className="text-gray-700 leading-relaxed mb-4">
                "Be ambitious in your goals, make any first step, allow yourself time to develop a new lifestyle, try different things to see what works for you and be patient with yourself."
              </p>
              <div className="flex space-x-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i key={star} className="ri-star-fill text-yellow-400" />
                ))}
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-400 to-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold">J</span>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Julie, 50's</div>
                  <div className="text-gray-500 text-sm">Mum of 2 / Office Worker</div>
                </div>
              </div>
              
              <div className="mb-6 rounded-xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/8e8cc12d0a4e0011e73079a8a1f6a96c/e566371a5e1d1e42c7a870a0f734ce53.jpeg"
                  alt="Julie's transformation - before and after"
                  className="w-full h-64 object-cover object-top"
                />
              </div>
              
              <p className="text-gray-700 leading-relaxed mb-4">
                "I felt embarrassed to go to the gym &amp; kept putting it off, I didn't feel I belonged there as an overweight person. Glad I did, the people here made me feel welcome from day one and I've lost 25kg."
              </p>
              <div className="flex space-x-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i key={star} className="ri-star-fill text-yellow-400" />
                ))}
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <button className="text-[#382112] font-semibold hover:text-[#2d1a0e] cursor-pointer">
              READ MORE STORIES →
            </button>
          </div>
        </div>
      </section>

      {/* Problem Solutions Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">
              We Solve Real Fitness Challenges for Adults 40-70
            </h2>
            <p className="text-xl text-gray-600">Your obstacles, our age-appropriate solutions</p>
          </div>

          <div className="grid md:grid-cols-2 gap-16">
            {/* Problems */}
            <div className="space-y-6">
              <h3 className="text-3xl font-bold text-red-600 mb-8">Common Problems</h3>

              <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-r-lg">
                <div className="flex items-center space-x-4">
                  <span className="text-2xl">😰</span>
                  <h4 className="text-xl font-semibold text-gray-900">Anxious About Restarting?</h4>
                </div>
                <p className="text-gray-700 mt-2">
                  Feeling overwhelmed about getting back into fitness after years away
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-r-lg">
                <div className="flex items-center space-x-4">
                  <span className="text-2xl">⏰</span>
                  <h4 className="text-xl font-semibold text-gray-900">No Time with Career &amp; Family?</h4>
                </div>
                <p className="text-gray-700 mt-2">
                  Juggling work responsibilities and family commitments leaves no time for fitness
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-r-lg">
                <div className="flex items-center space-x-4">
                  <span className="text-2xl">❓</span>
                  <h4 className="text-xl font-semibold text-gray-900">Confused About Age-Appropriate Exercise?</h4>
                </div>
                <p className="text-gray-700 mt-2">
                  Unsure what exercises are safe and effective for your age and fitness level
                </p>
              </div>
            </div>

            {/* Solutions */}
            <div className="space-y-6">
              <h3 className="text-3xl font-bold text-[#382112] mb-8">Our Solutions</h3>

              <div className="bg-[#f5f0eb] border-l-4 border-[#382112] p-6 rounded-r-lg">
                <div className="flex items-center space-x-4">
                  <span className="text-2xl">💪</span>
                  <h4 className="text-xl font-semibold text-gray-900">
                    Confidence Building for 40+
                  </h4>
                </div>
                <p className="text-gray-700 mt-2">
                  Supportive environment with age-appropriate progressions designed for adults returning to fitness
                </p>
              </div>

              <div className="bg-[#f5f0eb] border-l-4 border-[#382112] p-6 rounded-r-lg">
                <div className="flex items-center space-x-4">
                  <span className="text-2xl">⚡</span>
                  <h4 className="text-xl font-semibold text-gray-900">Time-Efficient for Busy Lives</h4>
                </div>
                <p className="text-gray-700 mt-2">
                  Get results in less time. Sessions designed to respect your professional and family commitments
                </p>
              </div>

              <div className="bg-[#f5f0eb] border-l-4 border-[#382112] p-6 rounded-r-lg">
                <div className="flex items-center space-x-4">
                  <span className="text-2xl">👥</span>
                  <h4 className="text-xl font-semibold text-gray-900">
                    Expert Guidance for Your Age
                  </h4>
                </div>
                <p className="text-gray-700 mt-2">
                  Jose &amp; Ana's 20+ years expertise ensures safe, effective programming for adults 40-70
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pain Points Accordion */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Every Concern, Answered</h2>
            <p className="text-xl text-gray-600">We understand your fitness challenges</p>
          </div>

          <div className="space-y-4">
            {painPoints.map((point, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm">
                <button
                  onClick={() => handleAccordionToggle(index)}
                  className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <div className="flex items-center space-x-4">
                    <span className="text-2xl">{point.icon}</span>
                    <h3 className="text-xl font-semibold text-gray-900">{point.question}</h3>
                  </div>
                  <i
                    className={`ri-${activeAccordion === index ? 'subtract' : 'add'}-line text-2xl text-gray-400`}
                  />
                </button>
                {activeAccordion === index && (
                  <div className="px-6 pb-6">
                    <div className="bg-[#f5f0eb] p-4 rounded-lg ml-12">
                      <p className="text-gray-700 leading-relaxed">{point.answer}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Map & Location Section */}
      <section id="location" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">
              Visit Mana Fitness Solutions
            </h2>
            <p className="text-xl text-gray-600">Experience I.C.E. in person</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Location Info */}
            <div className="bg-white p-8 rounded-2xl shadow-lg space-y-6">
              <div className="flex items-start space-x-4">
                <i className="ri-map-pin-line text-[#382112] text-2xl mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Address</h3>
                  <p className="text-gray-700 text-lg">
                    20/270 Ferntree Gully Rd
                    <br />
                    Notting Hill, Victoria
                    <br />
                    Australia
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <i className="ri-time-line text-[#382112] text-2xl mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Operating Hours</h3>
                  <div className="text-gray-700 space-y-1">
                    <p>Monday - Thursday: 6:00 AM - 1:00 PM &amp; 4:30 PM - 8:00 PM</p>
                    <p>Friday: 6:00 AM - 1:00 PM</p>
                    <p>Saturday: 8:30 AM - 11:30 AM</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <i className="ri-phone-line text-[#382112] text-2xl" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Phone</h3>
                  <a
                    href="tel:0415396910"
                    className="text-[#382112] hover:text-[#2d1a0e] text-lg cursor-pointer"
                  >
                    0415 396 910
                  </a>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <i className="ri-mail-line text-[#382112] text-2xl" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                  <a
                    href="mailto:ana@manafitness.com.au"
                    className="text-[#382112] hover:text-[#2d1a0e] text-lg cursor-pointer"
                  >
                    ana@manafitness.com.au
                  </a>
                </div>
              </div>

              <div className="pt-4">
                <h3 className="font-semibold text-gray-900 mb-4">Follow Us</h3>
                <div className="flex space-x-4">
                  <a
                    href="https://www.instagram.com/manafit/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-[#382112] rounded-full flex items-center justify-center hover:bg-[#2d1a0e] transition-colors cursor-pointer"
                  >
                    <i className="ri-instagram-line text-white" />
                  </a>
                </div>
              </div>

              <button className="w-full bg-[#382112] text-white py-4 rounded-lg font-semibold hover:bg-[#2d1a0e] transition-colors whitespace-nowrap">
                GET DIRECTIONS
              </button>
            </div>

            {/* Map */}
            <div className="rounded-2xl overflow-hidden shadow-lg h-96">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3149.0234567890123!2d145.1234567!3d-37.8234567!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1234567890abcdef%3A0x1234567890abcdef!2s20%2F270%20Ferntree%20Gully%20Rd%2C%20Notting%20Hill%20VIC%203168%2C%20Australia!5e0!3m2!1sen!2sau!4v1647355331991!5m2!1sen!2sau"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Mana Fitness Solutions Location - Notting Hill"
              />
            </div>
          </div>

          <div className="mt-8 bg-[#382112] text-white p-4 rounded-lg text-center">
            <p className="text-lg">
              Free parking available • Accessible facilities • Shower facilities
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#382112] text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center space-x-3 mb-6">
              <img
                src="https://static.readdy.ai/image/8e8cc12d0a4e0011e73079a8a1f6a96c/305a90f4c7c1c2b3e931b09382ba17d7.png"
                alt="Mana Fitness Solutions"
                className="h-10 w-auto"
              />
              <span className="text-2xl font-bold">Mana Fitness Solutions</span>
            </div>
            <h3 className="text-3xl font-bold text-[#d4a574] mb-4">
              Ready to Transform Your Life in 7 Days?
            </h3>
            <p className="text-xl text-gray-200 mb-8">
              Join the I.C.E. Program and discover what thousands of others already know
            </p>
            <a
              href="https://form.typeform.com/to/slBoUFJW"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#d4a574] text-[#382112] px-10 py-5 rounded-lg text-xl font-bold hover:bg-[#c69660] transition-colors inline-block cursor-pointer whitespace-nowrap"
            >
              START YOUR FREE 7-DAY TRIAL
            </a>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#d4a574] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-[#382112] text-3xl" />
              </div>
              <h4 className="text-lg font-semibold mb-2">Time Efficient</h4>
              <p className="text-gray-300">Fast &amp; effective results in half the time</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#d4a574] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-fire-line text-[#382112] text-3xl" />
              </div>
              <h4 className="text-lg font-semibold mb-2">3x Fat Burning</h4>
              <p className="text-gray-300">Burn up to 3x more fat than traditional cardio</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#d4a574] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-[#382112] text-3xl" />
              </div>
              <h4 className="text-lg font-semibold mb-2">Science-Based</h4>
              <p className="text-gray-300">Proven results backed by research</p>
            </div>
          </div>

          <div className="border-t border-[#d4a574]/20 pt-8">
            <div className="flex flex-col items-center space-y-4 text-center">
              <div className="text-gray-300 text-sm">
                © 2024 Mana Fitness Solutions. Transform your life with I.C.E.
              </div>

              <div className="flex items-center space-x-6">
                <a href="tel:0415396910" className="text-gray-300 hover:text-[#d4a574] cursor-pointer">
                  0415 396 910
                </a>
                <a href="mailto:ana@manafitness.com.au" className="text-gray-300 hover:text-[#d4a574] cursor-pointer">
                  ana@manafitness.com.au
                </a>
              </div>

              <div className="flex items-center space-x-4 text-sm text-gray-300">
                <a href="https://readdy.ai/?ref=logo" className="hover:text-[#d4a574] cursor-pointer">
                  Powered by Readdy
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;